import React, { useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';
import { X, Bookmark, BookmarkCheck } from 'lucide-react'; // Added Bookmark icons
import CopyableId from '../common/CopyableId';
import { exportToCsv } from '../../utils/helpers';

const Lineups = () => {
  const {
    t,
    loadProgramById,
    // Lineups State from Context
    lineupsPostalCode, setLineupsPostalCode,
    lineupsMsoInput, setLineupsMsoInput,
    lineupsList,
    lineupsSelectedLineup,
    lineupsChannels,
    lineupsSelectedChannel,
    lineupsScheduleDates,
    lineupsSelectedDate,
    lineupsSchedulePrograms,
    lineupsChannelFilter, setLineupsChannelFilter,
    lineupsIsLoading,
    lineupsError,
    lineupSortField, setLineupSortField,
    lineupSortDirection, setLineupSortDirection,
    channelSortField, setChannelSortField,
    channelSortDirection, setChannelSortDirection,
    // Saved Programs Context
    savedProgramTmsIds,
    toggleSaveProgram,
    // Timezone from Context
    availableTimezones,
    userTimezoneId,
    // Lineups Functions from Context
    fetchLineupsByPostal,
    fetchLineupsByMso,
    selectLineup,
    selectChannel,
    selectDate,
    resetLineupsContextState,
    loadLineupStateFromParams
  } = useAppContext();

  const params = useParams(); // Get URL parameters
  const navigate = useNavigate(); // Get navigate function
  
  // Get timezone abbreviation for display
  const currentTimezone = availableTimezones.find(tz => tz.id === userTimezoneId);
  const timezoneAbbr = currentTimezone ? (currentTimezone.abbr || currentTimezone.name) : 'UTC';

  // Effect to load state from URL parameters on mount or when params change
  useEffect(() => {
    const consoleMessages = []; // For clearer debugging

    // Add a lock to prevent re-entry if loadLineupStateFromParams is already running for a deep link
    if (params.lineupId && lineupsIsLoading.lineups) {
      console.log("LineupsEffect: Early exit because lineupsIsLoading.lineups is true for a deep link.");
      return;
    }

    if (params.lineupId) {
      // Case 1: URL has a lineupId.
      let needsLoad = false;

      if (!lineupsSelectedLineup) {
        // No lineup selected in context, but URL has one.
        // This could be a deep link, or a transitional state after reset if params.lineupId is stale.
        // If resetLineupsContextState also navigates to '/lineups', params.lineupId should eventually clear.
        // We assume this is a valid case to load (e.g., deep link).
        needsLoad = true;
        consoleMessages.push(`No lineup in context, URL has lineupId=${params.lineupId}. Assuming deep link/initial load.`);
      } else if (params.lineupId !== lineupsSelectedLineup.lineupId) {
        // A lineup is selected in context, but it's different from the one in the URL. Load URL's.
        needsLoad = true;
        consoleMessages.push(`Context lineup ${lineupsSelectedLineup.lineupId} differs from URL ${params.lineupId}. Loading URL's lineup.`);
      } else {
        // Context lineup matches URL lineup (params.lineupId === lineupsSelectedLineup.lineupId).
        // Check for missing sub-data.
        consoleMessages.push(`Context lineup ${lineupsSelectedLineup.lineupId} matches URL. Checking sub-data.`);
        if (lineupsChannels.length === 0 && !lineupsIsLoading.channels) {
          needsLoad = true;
          consoleMessages.push("Channels missing for current lineup and not loading.");
        } else if (params.channelId) {
          // Only proceed to check channel-specific data if params.channelId is present
          const channelContextMatchesUrl = params.channelId === String(lineupsSelectedChannel?.prgSvcId) || params.channelId === String(lineupsSelectedChannel?.channelId);
          if (!channelContextMatchesUrl) {
            // If context channel doesn't match URL, it implies a need to load/select the URL's channel.
            // This is tricky because loadLineupStateFromParams handles selecting based on URL.
            // If lineupsIsLoading.channels is true, it might be loading the wrong one or transitioning.
            // For now, if they don't match, assume a load is needed unless specifically loading channels.
            // This part might need refinement if it causes issues, but the primary loop is dates/programs.
            needsLoad = true; // Or consider !lineupsIsLoading.channels here too if it makes sense.
            consoleMessages.push(`Channel ID in URL ${params.channelId} differs from context channel ${lineupsSelectedChannel?.prgSvcId || lineupsSelectedChannel?.channelId}. Loading.`);
          } else if (lineupsScheduleDates === null && lineupsSelectedChannel && !lineupsIsLoading.dates) {
            // Channel matches, but dates haven't been fetched yet (are null) and not currently loading
            needsLoad = true;
            consoleMessages.push("Schedule dates are null (not fetched yet) for current channel and not loading.");
          } else if (params.scheduleId && channelContextMatchesUrl) {
            // Only proceed to check schedule-specific data if params.scheduleId is present and channel matches
            const scheduleContextMatchesUrl = params.scheduleId === String(lineupsSelectedDate?.scheduleId);
            if (!scheduleContextMatchesUrl) {
              // Similar to channel mismatch, if schedule IDs don't match, a load might be needed.
              needsLoad = true; // Or consider !lineupsIsLoading.programs or !lineupsIsLoading.dates
              consoleMessages.push(`Schedule ID in URL ${params.scheduleId} differs from context schedule ${lineupsSelectedDate?.scheduleId}. Loading.`);
            } else if (lineupsSchedulePrograms === null && lineupsSelectedDate && !lineupsIsLoading.programs) {
              // Schedule matches, but programs are missing (null indicates not fetched yet) and not loading
              needsLoad = true;
              consoleMessages.push("Programs are null (not fetched yet) for current schedule and not loading.");
            }
          }
        }
      }

      consoleMessages.forEach(msg => console.log(`LineupsEffect: ${msg}`));
      if (needsLoad) {
        console.log(`LineupsEffect: Final decision: Needs load. Calling loadLineupStateFromParams for ${params.lineupId}.`);
        loadLineupStateFromParams(params);
      } else {
        console.log(`LineupsEffect: Final decision: No load needed for ${params.lineupId}. Context state appears consistent.`);
      }

    } else {
      // Case 2: No lineupId in URL (e.g., at /lineups).
      // Clear context selections if any exist, as they don't match the URL.
      if (lineupsSelectedLineup || lineupsSelectedChannel || lineupsSelectedDate) {
        console.log("LineupsEffect: No lineupId in URL, but context has selections. Resetting selections via selectLineup(null).");
        selectLineup(null); // This clears lineupsSelectedLineup and downstream data.
      } else {
        // console.log("LineupsEffect: No lineupId in URL, and context selections are clear. State is clean.");
      }
    }
  }, [
    params,
    loadLineupStateFromParams,
    lineupsSelectedLineup, lineupsChannels,
    lineupsSelectedChannel, lineupsScheduleDates,
    lineupsSelectedDate, lineupsSchedulePrograms,
    selectLineup,
    lineupsIsLoading.channels,
    lineupsIsLoading.dates,
    lineupsIsLoading.lineups,
    lineupsIsLoading.programs
  ]); // Rerun when URL params or relevant context state changes


  // Effect for debounced fetching based on postal code input
  useEffect(() => {
    // Only fetch if no lineup is selected (i.e., we are in the initial search phase)
    if (!lineupsSelectedLineup) {
      const timeoutId = setTimeout(() => {
        if (lineupsPostalCode.length >= 3) {
          fetchLineupsByPostal(lineupsPostalCode);
        } else if (lineupsPostalCode.length === 0 && lineupsMsoInput.length === 0) {
           // Clear results if both inputs are empty
           selectLineup(null); // Use selectLineup(null) to clear list and selections
        }
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [lineupsPostalCode, fetchLineupsByPostal, lineupsSelectedLineup, lineupsMsoInput.length, selectLineup]);

  // Effect for debounced fetching based on MSO input
  useEffect(() => {
    // Only fetch if no lineup is selected
    if (!lineupsSelectedLineup) {
      const timeoutId = setTimeout(() => {
        if (lineupsMsoInput.length >= 3) {
          fetchLineupsByMso(lineupsMsoInput);
        } else if (lineupsPostalCode.length === 0 && lineupsMsoInput.length === 0) {
           // Clear results if both inputs are empty
           selectLineup(null); // Use selectLineup(null) to clear list and selections
        }
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [lineupsMsoInput, fetchLineupsByMso, lineupsSelectedLineup, lineupsPostalCode.length, selectLineup]);

  // Effect to refresh schedule programs when timezone changes
  useEffect(() => {
    // Only refresh if we have a selected date (meaning we're viewing a schedule)
    if (lineupsSelectedDate && lineupsSelectedDate.scheduleId) {
      console.log("Timezone changed, refreshing schedule programs with new timezone");
      selectDate(lineupsSelectedDate); // This will re-fetch with the new timezone
    }
  }, [userTimezoneId]); // Only watch userTimezoneId, not selectDate or lineupsSelectedDate to avoid loops

  // Debug logging
  console.log("lineupsList:", lineupsList);
  
  // Extract the actual lineups array from the API response
  // If lineupsList is already an array, use it directly
  const actualLineupsList = Array.isArray(lineupsList) ? lineupsList : [];
  
  console.log("actualLineupsList after fix:", actualLineupsList);


  // --- Event Handlers ---

  // Handle Input Changes - Update context state and clear the other input
  const handlePostalCodeChange = (e) => {
    const value = e.target.value;
    setLineupsPostalCode(value);
    if (value) setLineupsMsoInput(''); // Clear MSO if postal is typed
    // Reset selections only if user clears input or types enough to trigger search
    if (value.length < 3) {
       selectLineup(null); // Clear selections and results list
       navigate('/lineups'); // Go back to base URL if input is cleared
    }
  };

  const handleMsoInputChange = (e) => {
    const value = e.target.value;
    setLineupsMsoInput(value);
    if (value) setLineupsPostalCode(''); // Clear postal if MSO is typed
    // Reset selections only if user clears input or types enough to trigger search
    if (value.length < 3) {
       selectLineup(null); // Clear selections and results list
       navigate('/lineups'); // Go back to base URL if input is cleared
    }
  };

  // Handle Lineup Selection - Update context and navigate
  const handleLineupClick = useCallback((lineup) => {
    if (lineupsSelectedLineup?.lineupId === lineup.lineupId) return; // Avoid re-selecting same lineup
    selectLineup(lineup); // Update context state (fetches channels)
    navigate(`/lineups/${lineup.lineupId}`); // Update URL
  }, [selectLineup, navigate, lineupsSelectedLineup]);

  // Handle Cancel Lineup Selection - Reset context and navigate
  const handleCancelLineupClick = useCallback((e) => {
    e.stopPropagation();
    resetLineupsContextState(); // Reset context state completely
    // navigate('/lineups'); // Already handled by resetLineupsContextState
  }, [resetLineupsContextState]);

  // Handle Channel Selection - Update context and navigate
  const handleChannelClick = useCallback((channel) => {
    if (lineupsSelectedChannel?.channelId === channel.channelId) return; // Avoid re-selecting same channel
    selectChannel(channel); // Update context state (fetches dates)
    // Use prgSvcId if available, otherwise channelId for URL robustness
    const channelIdentifier = channel.prgSvcId || channel.channelId;
    navigate(`/lineups/${lineupsSelectedLineup.lineupId}/channels/${channelIdentifier}`); // Update URL
  }, [selectChannel, navigate, lineupsSelectedLineup, lineupsSelectedChannel]);

  // Handle Cancel Channel Selection - Update context and navigate
  const handleCancelChannelClick = useCallback((e) => {
    e.stopPropagation();
    selectChannel(null); // Clear channel selection in context (keeps lineup)
    navigate(`/lineups/${lineupsSelectedLineup.lineupId}`); // Navigate back to lineup level
  }, [selectChannel, navigate, lineupsSelectedLineup]);

  // Handle Date Selection - Update context and navigate
  const handleDateClick = useCallback((scheduleDate) => {
    if (lineupsSelectedDate?.scheduleId === scheduleDate.scheduleId) return; // Avoid re-selecting same date
    selectDate(scheduleDate); // Update context state (fetches programs)
    const channelIdentifier = lineupsSelectedChannel.prgSvcId || lineupsSelectedChannel.channelId;
    navigate(`/lineups/${lineupsSelectedLineup.lineupId}/channels/${channelIdentifier}/schedules/${scheduleDate.scheduleId}`); // Update URL
  }, [selectDate, navigate, lineupsSelectedLineup, lineupsSelectedChannel, lineupsSelectedDate]);

  // Handle Cancel Date Selection - Update context and navigate
  const handleCancelDateClick = useCallback((e) => {
    e.stopPropagation();
    selectDate(null); // Clear date selection in context (keeps channel)
    const channelIdentifier = lineupsSelectedChannel.prgSvcId || lineupsSelectedChannel.channelId;
    navigate(`/lineups/${lineupsSelectedLineup.lineupId}/channels/${channelIdentifier}`); // Navigate back to channel level
  }, [selectDate, navigate, lineupsSelectedLineup, lineupsSelectedChannel]);

  // Handle Program Title Click - Use context function to navigate
  const handleProgramTitleClick = useCallback((tmsId) => {
    if (tmsId) {
      loadProgramById(tmsId);
    }
  }, [loadProgramById]);

  // --- Utility Functions (Keep as they are local helpers) ---
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      // Assuming dateString is like 'YYYY-MM-DD' or can be parsed directly
      const date = new Date(dateString + 'T00:00:00'); // Add time part to avoid timezone issues if just date
      if (isNaN(date)) return dateString; // Handle invalid date strings
      // Ideally we should use locale from context/i18n for toLocaleDateString, defaulting to en-US for now if not available easily here
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch (error) {
      console.error("Error formatting date:", dateString, error);
      return dateString; // Fallback
    }
  };
  
  // Format date for programs table (remove year, show MM-DD format)
  const formatProgramDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      // dateString is like '2025-11-27', extract MM-DD
      const parts = dateString.split('-');
      if (parts.length >= 3) {
        return `${parts[1]}-${parts[2]}`; // Return MM-DD
      }
      return dateString;
    } catch (error) {
      console.error("Error formatting program date:", dateString, error);
      return dateString;
    }
  };
  const formatDuration = (durString) => {
    if (!durString) return 'N/A';
    const hourMatch = durString.match(/(\d+)H/);
    const minuteMatch = durString.match(/(\d+)M/);
    const hours = hourMatch ? parseInt(hourMatch[1], 10) : 0;
    const minutes = minuteMatch ? parseInt(minuteMatch[1], 10) : 0;
    if (hours === 0) return t('lineups.duration.min', { minutes });
    if (minutes === 0) return t('lineups.duration.hr', { hours });
    return t('lineups.duration.hrMin', { hours, minutes });
  };

  // --- Sorting Logic ---
  const handleLineupSort = useCallback((field) => {
    const direction = (field === lineupSortField && lineupSortDirection === 'asc') ? 'desc' : 'asc';
    setLineupSortField(field);
    setLineupSortDirection(direction);
  }, [lineupSortField, lineupSortDirection, setLineupSortField, setLineupSortDirection]);

  const handleChannelSort = useCallback((field) => {
    const direction = (field === channelSortField && channelSortDirection === 'asc') ? 'desc' : 'asc';
    setChannelSortField(field);
    setChannelSortDirection(direction);
  }, [channelSortField, channelSortDirection, setChannelSortField, setChannelSortDirection]);

  // Generic sort function remains the same
  const sortData = (data, field, direction, numericFields = [], dateFields = []) => {
    return [...data].sort((a, b) => {
      const aValue = a[field] || '';
      const bValue = b[field] || '';
      if (dateFields.includes(field)) {
        const aDate = aValue ? new Date(aValue) : new Date(0); const bDate = bValue ? new Date(bValue) : new Date(0);
        return direction === 'asc' ? aDate - bDate : bDate - aDate;
      }
      if (numericFields.includes(field)) {
        const aNum = !isNaN(parseFloat(aValue)) ? parseFloat(aValue) : -Infinity; const bNum = !isNaN(parseFloat(bValue)) ? parseFloat(bValue) : -Infinity;
        return direction === 'asc' ? aNum - bNum : bNum - aNum;
      }
      return direction === 'asc' ? String(aValue).localeCompare(String(bValue)) : String(bValue).localeCompare(String(aValue));
    });
  };

  // Determine which list of lineups to display/sort
  // If a lineup is selected, show only that one. Otherwise, show the search results list.
  const displayLineups = lineupsSelectedLineup ? [lineupsSelectedLineup] : actualLineupsList;
  const sortedLineups = sortData(displayLineups, lineupSortField, lineupSortDirection, ['lineupId'], ['updateDate']);

  // Apply filtering *before* sorting channels
  const filteredSortedChannels = sortData(lineupsChannels, channelSortField, channelSortDirection, ['channelNumber', 'prgSvcId'])
    .filter(channel => {
      if (!lineupsChannelFilter) return true;
      const lowerCaseFilter = lineupsChannelFilter.toLowerCase();
      // Check relevant fields for the filter term
      return (
        String(channel.channelNumber).toLowerCase().includes(lowerCaseFilter) ||
        (channel.callsign && String(channel.callsign).toLowerCase().includes(lowerCaseFilter)) ||
        (channel.name && String(channel.name).toLowerCase().includes(lowerCaseFilter)) ||
        String(channel.prgSvcId).toLowerCase().includes(lowerCaseFilter) ||
        (channel.city && String(channel.city).toLowerCase().includes(lowerCaseFilter)) ||
        (channel.state && String(channel.state).toLowerCase().includes(lowerCaseFilter)) ||
        (channel.timeZone && String(channel.timeZone).toLowerCase().includes(lowerCaseFilter)) ||
        (channel.videoType && String(channel.videoType).toLowerCase().includes(lowerCaseFilter))
      );
    });

  const handleExportChannels = () => {
    if (!filteredSortedChannels || filteredSortedChannels.length === 0) return;

    const rows = filteredSortedChannels.map((channel) => ({
      'Channel #': channel.channelNumber || '',
      Callsign: channel.callsign || '',
      Name: channel.name || '',
      'Service ID': channel.prgSvcId || '',
      City: channel.city || '',
      State: channel.state || '',
      'Time Zone': channel.timeZone || '',
      'Video Type': channel.videoType || '',
    }));

    const lineupId = lineupsSelectedLineup?.lineupId || 'lineup';
    exportToCsv(rows, `${lineupId}_channels.csv`);
  };

  // Determine which list of schedule dates to display
  // If a date is selected, show only that one. Otherwise, show all fetched dates.
  // Note: We store the selected date object in lineupsSelectedDate
  // Ensure lineupsScheduleDates is treated as an empty array if null for display purposes initially
  const displayScheduleDates = lineupsSelectedDate ? [lineupsSelectedDate] : (lineupsScheduleDates || []);

  // Get previous and next dates for navigation
  const getPreviousAndNextDates = useCallback(() => {
    if (!lineupsSelectedDate || !lineupsScheduleDates || lineupsScheduleDates.length === 0) {
      return { previousDate: null, nextDate: null };
    }
    
    const currentIndex = lineupsScheduleDates.findIndex(
      date => date.scheduleId === lineupsSelectedDate.scheduleId
    );
    
    if (currentIndex === -1) {
      return { previousDate: null, nextDate: null };
    }
    
    const previousDate = currentIndex > 0 ? lineupsScheduleDates[currentIndex - 1] : null;
    const nextDate = currentIndex < lineupsScheduleDates.length - 1 ? lineupsScheduleDates[currentIndex + 1] : null;
    
    return { previousDate, nextDate };
  }, [lineupsSelectedDate, lineupsScheduleDates]);

  const { previousDate, nextDate } = getPreviousAndNextDates();


  // --- Render Logic ---
  return (
    <div className="flex flex-col flex-grow overflow-auto p-6">
      <h2 className="text-xl font-semibold mb-4 text-left shrink-0">{t('lineups.title')}</h2>

      {/* --- Initial Search Section (Show only if no lineup is selected) --- */}
      {!lineupsSelectedLineup && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6 shrink-0">
            {/* Postal Code Input */}
            {/* Postal Code Input */}
            <div>
              <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700 mb-1">{t('lineups.enterPostalCode')}</label>
              <input type="text" id="postalCode" className="w-full px-3 py-2 border border-gray-300 rounded text-sm" placeholder={t('lineups.postalCodePlaceholder')} value={lineupsPostalCode} onChange={handlePostalCodeChange} />
              <p className="text-xs text-gray-500 mt-1">{t('lineups.postalCodeHelp')}</p>
            </div>
            {/* MSO Input */}
            <div>
              <label htmlFor="msoInput" className="block text-sm font-medium text-gray-700 mb-1">{t('lineups.enterMso')}</label>
              <input type="text" id="msoInput" className="w-full px-3 py-2 border border-gray-300 rounded text-sm" placeholder={t('lineups.msoPlaceholder')} value={lineupsMsoInput} onChange={handleMsoInputChange} />
              <p className="text-xs text-gray-500 mt-1">{t('lineups.msoHelp')}</p>
            </div>
          </div>
          {/* Initial Loading/Error/No Results */}
          {lineupsIsLoading.lineups && (<div className="flex justify-center items-center h-32"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div></div>)}
          {lineupsError.lineups && (<div className="p-4 text-center text-red-500"><p>{lineupsError.lineups}</p></div>)}
          {!lineupsIsLoading.lineups && !lineupsError.lineups && lineupsPostalCode.length >= 3 && actualLineupsList.length === 0 && (<div className="p-4 text-center text-gray-500"><p>{t('lineups.noLineupsPostal')}</p></div>)}
          {!lineupsIsLoading.lineups && !lineupsError.lineups && lineupsMsoInput.length >= 3 && actualLineupsList.length === 0 && (<div className="p-4 text-center text-gray-500"><p>{t('lineups.noLineupsMso')}</p></div>)}
          {/* Initial Lineup List Table */}
          {!lineupsIsLoading.lineups && !lineupsError.lineups && actualLineupsList.length > 0 && (
             <div className="overflow-x-auto overflow-y-auto mb-8">
               <table className="w-full bg-white border border-gray-200 text-left">
                 {/* Lineup Table Header (using context sort state) */}
                 <thead className="bg-gray-50 sticky top-0 z-10">
                   <tr>
                     <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">{t('lineups.columns.actions')}</th>
                     <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100" onClick={() => handleLineupSort('lineupId')}>
                       <div className="flex items-center">{t('lineups.columns.lineupId')}{lineupSortField === 'lineupId' && <span className="ml-1">{lineupSortDirection === 'asc' ? '▲' : '▼'}</span>}</div>
                     </th>
                     <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100" onClick={() => handleLineupSort('headend')}>
                       <div className="flex items-center">{t('lineups.columns.headend')}{lineupSortField === 'headend' && <span className="ml-1">{lineupSortDirection === 'asc' ? '▲' : '▼'}</span>}</div>
                     </th>
                     <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100" onClick={() => handleLineupSort('location')}>
                       <div className="flex items-center">{t('lineups.columns.location')}{lineupSortField === 'location' && <span className="ml-1">{lineupSortDirection === 'asc' ? '▲' : '▼'}</span>}</div>
                     </th>
                     <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100" onClick={() => handleLineupSort('mso')}>
                       <div className="flex items-center">{t('lineups.columns.mso')}{lineupSortField === 'mso' && <span className="ml-1">{lineupSortDirection === 'asc' ? '▲' : '▼'}</span>}</div>
                     </th>
                     <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100" onClick={() => handleLineupSort('type')}>
                       <div className="flex items-center">{t('lineups.columns.type')}{lineupSortField === 'type' && <span className="ml-1">{lineupSortDirection === 'asc' ? '▲' : '▼'}</span>}</div>
                     </th>
                     <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100" onClick={() => handleLineupSort('country')}>
                       <div className="flex items-center">{t('lineups.columns.country')}{lineupSortField === 'country' && <span className="ml-1">{lineupSortDirection === 'asc' ? '▲' : '▼'}</span>}</div>
                     </th>
                     <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100" onClick={() => handleLineupSort('updateDate')}>
                       <div className="flex items-center">{t('lineups.columns.updateDate')}{lineupSortField === 'updateDate' && <span className="ml-1">{lineupSortDirection === 'asc' ? '▲' : '▼'}</span>}</div>
                     </th>
                   </tr>
                 </thead>
                 <tbody className="divide-y divide-gray-200 text-left">
                   {/* Use sortedLineups which contains either the search results or the single selected lineup */}
                   {sortedLineups.map((lineup, index) => (
                     <tr key={lineup.lineupId || index} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-100 cursor-pointer transition-colors`} onClick={() => handleLineupClick(lineup)}>
                       <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">{/* Action placeholder */}</td>
                       <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">{lineup.lineupId || 'N/A'}</td>
                       <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">{lineup.headend || 'N/A'}</td>
                       <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">{lineup.location || 'N/A'}</td>
                       <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">{lineup.mso || 'N/A'}</td>
                       <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">{lineup.type || 'N/A'}</td>
                       <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">{lineup.country || 'N/A'}</td>
                       <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">{formatDate(lineup.updateDate)}</td>
                     </tr>
                   ))}
                 </tbody>
               </table>
             </div>
          )}
        </>
      )}

      {/* --- Selected Lineup Info (Show only if a lineup is selected) --- */}
      {lineupsSelectedLineup && (
        <div className="mb-4"> {/* Reduced margin */}
          <h3 className="text-lg font-medium mb-2 text-left flex items-center">
            {t('lineups.selectedLineup', { headend: lineupsSelectedLineup.headend || t('lineups.loading'), lineupId: lineupsSelectedLineup.lineupId })}
            <button className="ml-2 p-1 rounded-full bg-gray-200 hover:bg-gray-300 focus:outline-none align-middle" onClick={handleCancelLineupClick} title={t('lineups.tooltips.resetSearch')}>
              <X size={16} />
            </button>
          </h3>
        </div>
      )}

      {/* --- Channels Section (Show only if a lineup is selected) --- */}
      {lineupsSelectedLineup && (
        <div className="mt-0"> {/* Removed top margin */}
          {/* Channel Header with Title and Filter Input */}
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-left flex items-center">
              {lineupsSelectedChannel
                ? t('lineups.selectedChannel', { name: lineupsSelectedChannel.name || lineupsSelectedChannel.callsign, number: lineupsSelectedChannel.channelNumber || 'N/A' })
                : t('lineups.channelsFor', { headend: lineupsSelectedLineup.headend || t('lineups.loading'), lineupId: lineupsSelectedLineup.lineupId })
              }
              {lineupsSelectedChannel && (
                <button className="ml-2 p-1 rounded-full bg-gray-200 hover:bg-gray-300 focus:outline-none align-middle" onClick={handleCancelChannelClick} title={t('lineups.tooltips.showAllChannels')}>
                  <X size={16} />
                </button>
              )}
            </h3>
            <div className="flex items-center gap-2">
              {/* Filter input shown only when viewing the list and channels are loaded */}
              {!lineupsSelectedChannel && lineupsChannels.length > 0 && !lineupsIsLoading.channels && (
                <input
                  type="text"
                  placeholder={t('lineups.filterChannels')}
                  className="px-3 py-1 border border-gray-300 rounded text-sm ml-4"
                  value={lineupsChannelFilter}
                  onChange={(e) => setLineupsChannelFilter(e.target.value)}
                />
              )}
              {!lineupsSelectedChannel && lineupsChannels.length > 0 && !lineupsIsLoading.channels && (
                <button
                  className="px-3 py-1 border border-gray-300 rounded-sm bg-white text-gray-700 hover:bg-gray-100 text-xs"
                  onClick={handleExportChannels}
                >
                  {t('lineups.exportChannels')}
                </button>
              )}
            </div>
          </div>

          {/* Channel Loading/Error/No Results */}
          {lineupsIsLoading.channels && (<div className="flex justify-center items-center h-32"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div></div>)}
          {lineupsError.channels && (<div className="p-4 text-center text-red-500"><p>{lineupsError.channels}</p></div>)}
          {!lineupsIsLoading.channels && !lineupsError.channels && lineupsChannels.length === 0 && (<div className="p-4 text-center text-gray-500"><p>{t('lineups.noChannels')}</p></div>)}

          {/* Channels Table (Show if channels loaded, no error, and no channel is selected) */}
          {!lineupsIsLoading.channels && !lineupsError.channels && lineupsChannels.length > 0 && !lineupsSelectedChannel && (
            <div className="overflow-x-auto overflow-y-auto">{/* Allow table to grow with available space */}
              <table className="w-full bg-white border border-gray-200 text-left">
                {/* Header always shown when table is visible */}
                {/* {!lineupsSelectedChannel && ( */} {/* Removed condition */}
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">{/* Actions */}</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100" onClick={() => handleChannelSort('channelNumber')}>
                        <div className="flex items-center">{t('lineups.columns.channelNumber')}{channelSortField === 'channelNumber' && <span className="ml-1">{channelSortDirection === 'asc' ? '▲' : '▼'}</span>}</div>
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100" onClick={() => handleChannelSort('callsign')}>
                        <div className="flex items-center">{t('lineups.columns.callsign')}{channelSortField === 'callsign' && <span className="ml-1">{channelSortDirection === 'asc' ? '▲' : '▼'}</span>}</div>
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100" onClick={() => handleChannelSort('name')}>
                        <div className="flex items-center">{t('lineups.columns.name')}{channelSortField === 'name' && <span className="ml-1">{channelSortDirection === 'asc' ? '▲' : '▼'}</span>}</div>
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100" onClick={() => handleChannelSort('prgSvcId')}>
                        <div className="flex items-center">{t('lineups.columns.serviceId')}{channelSortField === 'prgSvcId' && <span className="ml-1">{channelSortDirection === 'asc' ? '▲' : '▼'}</span>}</div>
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100" onClick={() => handleChannelSort('city')}>
                        <div className="flex items-center">{t('lineups.columns.city')}{channelSortField === 'city' && <span className="ml-1">{channelSortDirection === 'asc' ? '▲' : '▼'}</span>}</div>
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100" onClick={() => handleChannelSort('state')}>
                        <div className="flex items-center">{t('lineups.columns.state')}{channelSortField === 'state' && <span className="ml-1">{channelSortDirection === 'asc' ? '▲' : '▼'}</span>}</div>
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100" onClick={() => handleChannelSort('timeZone')}>
                        <div className="flex items-center">{t('lineups.columns.timeZone')}{channelSortField === 'timeZone' && <span className="ml-1">{channelSortDirection === 'asc' ? '▲' : '▼'}</span>}</div>
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100" onClick={() => handleChannelSort('videoType')}>
                        <div className="flex items-center">{t('lineups.columns.videoType')}{channelSortField === 'videoType' && <span className="ml-1">{channelSortDirection === 'asc' ? '▲' : '▼'}</span>}</div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200 text-left">
                    {filteredSortedChannels
                      .map((channel) => (
                        <tr 
                          key={channel.prgSvcId} 
                          className={`hover:bg-blue-50 cursor-pointer ${lineupsSelectedChannel?.prgSvcId === channel.prgSvcId ? 'bg-blue-100' : ''}`}
                          onClick={() => handleChannelClick(channel)}
                        >
                          <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">{/* Action cell empty */}</td>
                          <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">{channel.channelNumber || 'N/A'}</td>
                          <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">{channel.callsign || 'N/A'}</td>
                          <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">{channel.name || 'N/A'}</td>
                          <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">{channel.prgSvcId || 'N/A'}</td>
                          <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">{channel.city || 'N/A'}</td>
                          <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">{channel.state || 'N/A'}</td>
                          <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">{channel.timeZone || 'N/A'}</td>
                          <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">{channel.videoType || 'N/A'}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

      {/* --- Schedule Dates Section (Show only if a channel is selected) --- */}
      {lineupsSelectedChannel && (
        <div className="mt-8">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-left flex items-center">
               {lineupsSelectedDate
                 ? t('lineups.selectedDate', { date: lineupsSelectedDate.date })
                 : t('lineups.scheduleDatesFor', { name: lineupsSelectedChannel.name || lineupsSelectedChannel.callsign, number: lineupsSelectedChannel.channelNumber || 'N/A' })
               }
               {lineupsSelectedDate && (
                  <button className="ml-2 p-1 rounded-full bg-gray-200 hover:bg-gray-300 focus:outline-none align-middle" onClick={handleCancelDateClick} title={t('lineups.tooltips.showAllDates')}>
                      <X size={16} />
                  </button>
               )}
            </h3>
            {/* Date Navigation - Right Side */}
            {lineupsSelectedDate && (
              <div className="flex gap-2">
                {previousDate && (
                  <button
                    className="px-4 py-2 rounded-md bg-blue-100 hover:bg-blue-200 text-blue-800 transition-colors"
                    onClick={() => handleDateClick(previousDate)}
                    title={`Previous date: ${previousDate.date}`}
                  >
                    {formatProgramDate(previousDate.date)}
                  </button>
                )}
                {nextDate && (
                  <button
                    className="px-4 py-2 rounded-md bg-blue-100 hover:bg-blue-200 text-blue-800 transition-colors"
                    onClick={() => handleDateClick(nextDate)}
                    title={`Next date: ${nextDate.date}`}
                  >
                    {formatProgramDate(nextDate.date)}
                  </button>
                )}
              </div>
            )}
          </div>
          {/* Loading/Error/No Results for Dates */}
          {lineupsIsLoading.dates && (<div className="flex justify-center items-center h-32"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div></div>)}
          {lineupsError.dates && (<div className="p-4 text-center text-red-500"><p>{lineupsError.dates}</p></div>)}
          {/* Show "No schedule dates found" if dates have been loaded (i.e., not null) and the array is empty */}
          {!lineupsIsLoading.dates && !lineupsError.dates && lineupsScheduleDates && lineupsScheduleDates.length === 0 && (<div className="p-4 text-center text-gray-500"><p>{t('lineups.noDates')}</p></div>)}

          {/* Date Buttons (Show only if dates are loaded (not null), not loading, no error, and no date is selected) */}
          {!lineupsSelectedDate && !lineupsIsLoading.dates && !lineupsError.dates && lineupsScheduleDates && lineupsScheduleDates.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-8 items-center shrink-0">
              {/* Use displayScheduleDates which is either all dates or just the selected one */}
              {displayScheduleDates.map((scheduleDate) => {
                // Check if this date is today
                const today = new Date();
                const todayString = today.getFullYear() + '-' + 
                  String(today.getMonth() + 1).padStart(2, '0') + '-' + 
                  String(today.getDate()).padStart(2, '0');
                
                // scheduleDate.date might be in YYYY-MM-DD or MM-DD format
                // Try to match both formats
                let isToday = false;
                if (scheduleDate.date) {
                  // Check if it's full format (YYYY-MM-DD)
                  isToday = scheduleDate.date === todayString;
                  
                  // If not matched, check if it ends with today's MM-DD
                  if (!isToday) {
                    const todayMMDD = String(today.getMonth() + 1).padStart(2, '0') + '-' + 
                      String(today.getDate()).padStart(2, '0');
                    isToday = scheduleDate.date.endsWith(todayMMDD) || scheduleDate.date === todayMMDD;
                  }
                }
                
                console.log('Date comparison:', scheduleDate.date, 'vs today:', todayString, 'isToday:', isToday);
                
                return (
                  <div key={scheduleDate.scheduleId} className="flex items-center gap-1">
                    <button
                      className={`px-4 py-2 rounded-md transition-colors ${
                        lineupsSelectedDate?.scheduleId === scheduleDate.scheduleId 
                          ? 'bg-blue-500 text-white' 
                          : isToday
                            ? 'bg-green-200 hover:bg-green-300 text-green-900 font-semibold'
                            : 'bg-blue-100 hover:bg-blue-200 text-blue-800'
                      }`}
                      onClick={() => handleDateClick(scheduleDate)}
                    >
                      {scheduleDate.date} {/* Display the date */}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}


      {/* --- Schedule Programs Section (Show only if a date is selected) --- */}
      {lineupsSelectedDate && (
        <div className="mt-4 flex-grow flex flex-col overflow-hidden">
          {/* Header is part of the Dates section now */}
          {/* Loading/Error/No Results for Programs */}
          {lineupsIsLoading.programs && (<div className="flex justify-center items-center h-32"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div></div>)}
          {lineupsError.programs && (<div className="p-4 text-center text-red-500 shrink-0"><p>{lineupsError.programs}</p></div>)}
          {/* Show "No programs found" if programs are fetched (not null) and empty */}
          {!lineupsIsLoading.programs && !lineupsError.programs && lineupsSchedulePrograms && lineupsSchedulePrograms.length === 0 && (<div className="p-4 text-center text-gray-500 shrink-0"><p>{t('lineups.noPrograms')}</p></div>)}

          {/* Programs Table (Show if programs loaded (not null), no error, and has items) */}
          {!lineupsIsLoading.programs && !lineupsError.programs && lineupsSchedulePrograms && lineupsSchedulePrograms.length > 0 && (
            <div className="overflow-y-auto flex-grow">
              <table className="w-full bg-white border border-gray-200 text-left">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">{t('lineups.columns.date')}</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">{t('lineups.columns.time')} ({timezoneAbbr})</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">{t('lineups.columns.programTitle')}</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">{t('lineups.columns.episodeTitle')}</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">{t('lineups.columns.duration')}</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">{t('lineups.columns.tmsId')}</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">{t('lineups.columns.rootId')}</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">{t('lineups.columns.language')}</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">{t('lineups.columns.qualifiers')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 text-left">
                  {/* Ensure lineupsSchedulePrograms is an array before mapping */}
                  {(lineupsSchedulePrograms || []).map((program, index) => (
                    <tr key={program.TMSId + '-' + program.time + '-' + index} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-100`} > {/* Improved key */}
                      <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">{formatProgramDate(program.date)}</td>
                      <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">{program.time || 'N/A'}</td>
                      {/* Make Title clickable */}
                      <td
                        className="px-4 py-2 text-sm text-blue-600 hover:underline cursor-pointer"
                        onClick={() => handleProgramTitleClick(program.TMSId)} // Use handler
                        title={program.TMSId ? t('lineups.tooltips.viewDetails', { title: program.title }) : ''}
                      >
                        {program.title || 'N/A'}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">{program.ep_title || ''}</td>
                      <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">{formatDuration(program.dur)}</td>
                      {/* Make TMS ID cell clickable */}
                      <td
                        className="px-4 py-2 text-sm text-gray-500 flex items-center"
                      >
                        {program.TMSId ? (
                          <>
                            <CopyableId value={program.TMSId} onClickOverride={(e) => { e.stopPropagation(); handleProgramTitleClick(program.TMSId); }} />
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleSaveProgram(program.TMSId);
                              }}
                              className="ml-2 p-1 rounded hover:bg-gray-200"
                              title={savedProgramTmsIds.includes(program.TMSId) ? t('lineups.tooltips.unsave') : t('lineups.tooltips.save')}
                            >
                              {savedProgramTmsIds.includes(program.TMSId) ? (
                                <BookmarkCheck size={18} className="text-red-600" />
                              ) : (
                                <Bookmark size={18} className="text-gray-500 hover:text-red-600" />
                              )}
                            </button>
                          </>
                        ) : 'N/A'}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                        {program.rootId ? <CopyableId value={program.rootId} /> : 'N/A'}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">{program.lang || 'N/A'}</td>
                      <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">{program.quals || 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Lineups;
